import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { cn } from "@/utils/cn";
import { Sidebar } from "./Sidebar";
import { AdminHeader } from "./AdminHeader";
import { AdminLayoutSkeleton } from "./AdminLayoutSkeleton";
import { useFilteredNavItems } from "@/hooks/useFilteredNavItems";
import { NoAccessPage } from "@/pages";

const MOBILE_BREAKPOINT = 768;

export function AdminLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const { hasNoAccess, isLoading } = useFilteredNavItems();

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      // Close sidebar by default on mobile
      if (mobile) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close mobile sidebar when navigating
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleToggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleCloseSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // These must come after every hook: bailing out earlier changes the hook count between
  // renders (isLoading flips false) and React throws "Rendered more hooks...".

  // Hold the shell until the permissions are known. Rendering it first shows the sidebar
  // and header of an app the user may not be allowed into, then rips them away.
  if (isLoading) {
    return <AdminLayoutSkeleton />;
  }

  if (hasNoAccess) {
    return <NoAccessPage />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30"
          onClick={handleCloseSidebar}
        />
      )}

      <Sidebar
        collapsed={isMobile ? false : sidebarCollapsed}
        isOpen={isMobile ? sidebarOpen : true}
        isMobile={isMobile}
        onToggle={handleToggleSidebar}
        onClose={handleCloseSidebar}
      />

      <AdminHeader
        sidebarCollapsed={sidebarCollapsed}
        isMobile={isMobile}
        onMenuClick={handleToggleSidebar}
      />

      <main
        className={cn(
          "pt-16 min-h-screen transition-all duration-300",
          isMobile ? "pl-0" : (sidebarCollapsed ? "pl-16" : "pl-64")
        )}
      >
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
